<template>
  <div class="loading" :class="{ 'is-retry': showRetry }">
    <div v-if="showRetry" class="loading__retry">
      <p class="loading__message">{{ $t("assistantCatHome.connectionError.message") }}</p>
      <button
        type="button"
        class="loading__retry-button"
        :aria-label="$t('assistantCatHome.connectionError.retry')"
        @click="$emit('retry')"
      >
        <icon-refresh />
      </button>
      <p class="loading__retry-label">{{ $t("assistantCatHome.connectionError.retry") }}</p>
    </div>
    <div v-else class="loading__idle">
      <p class="loading__text">{{ $t("assistantCatHome.loading") }}</p>
      <div class="loading__paws" aria-hidden="true">
        <icon-assist v-if="pawCount >= 1" class="loading__paw loading__paw--1" />
        <icon-assist v-if="pawCount >= 2" class="loading__paw loading__paw--2" />
        <icon-assist v-if="pawCount >= 3" class="loading__paw loading__paw--3" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import IconAssist from "~icons/figma/Assist";
import IconRefresh from "~icons/figma/Refresh";

export default Vue.extend({
  name: "AssistantCatHomeLoading",
  components: {
    IconAssist,
    IconRefresh,
  },
  props: {
    pawCount: {
      type: Number,
      default: 0,
    },
    showRetry: {
      type: Boolean,
      default: false,
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.loading {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-normal);
  color: var(--color-text-normal);
  opacity: 1;
  transition: opacity 280ms ease-out;

  &:not(.is-retry) {
    align-items: flex-end;
    justify-content: flex-end;
    padding: 0 calc(24px + env(safe-area-inset-right, 0px)) calc(24px + env(safe-area-inset-bottom, 0px)) 16px;
  }

  &.is-fading {
    opacity: 0;
    pointer-events: none;
  }
}

.loading__idle {
  display: flex;
  flex-shrink: 0;
  align-items: flex-end;
  max-width: 100%;
}

.loading__text {
  margin: 0 16px 0 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.loading__paws {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 48px;
  overflow: visible;
}

.loading__paw {
  position: absolute;
  width: 16px;
  height: 16px;
  font-size: 16px;
  color: var(--color-text-normal);

  &--1 {
    right: 0;
    bottom: 0;
  }

  &--2 {
    right: 20px;
    bottom: 12px;
  }

  &--3 {
    right: 4px;
    bottom: 26px;
  }
}

.loading__retry {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 360px;
  padding: 0 32px;
  text-align: center;
}

.loading__message {
  margin: 0;
  white-space: pre-line;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.loading__retry-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-top: 16px;
  padding: 0;
  border: none;
  border-radius: var(--radius-circle);
  background: transparent;
  color: var(--color-text-normal);
  font-size: 32px;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.loading__retry-label {
  margin: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}
</style>
